import {
  MdOutlineClose,
  MdAccountBalance,
  MdReceipt,
  MdAttachment,
} from "react-icons/md";
import { formatDateByFormat } from "../../../_helpers";

const RedeemDetailsModal = ({ isOpen, closeModel, ele }) => {
  if (!isOpen) {
    return null;
  }

  const { referenceId } = ele;

  const formatCurrency = (amount, symbol = "₹", show = false) => {
    const formattedAmount = Number(amount).toFixed(4);

    return show
      ? `${formattedAmount} ${symbol}`
      : `${symbol} ${formattedAmount}`;
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-40 bg-black bg-opacity-50">
      <div className="relative bg-[#FAFAFA] border-[#D2D2D2] text-black rounded-[6px] lg:max-w-4xl w-11/12 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start px-6 pt-4">
          <div>
            <h1 className="text-xl font-bold text-black">
              Redeem Request Details
            </h1>
          </div>
          <div
            className="w-8 h-8 cursor-pointer rounded-full bg-[#ECECEC] hover:bg-[#ECECEC]/80 flex justify-center items-center"
            onClick={closeModel}
          >
            <MdOutlineClose title="Close" />
          </div>
        </div>

        <div className="p-6">
          {/* Summary Section */}
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center gap-2 mb-3">
              <MdReceipt className="text-blue-600" />
              <h2 className="text-lg font-semibold text-blue-800">
                Redemption Summary
              </h2>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-gray-600">Total Redeem Amount</div>
                <div className="text-2xl font-bold text-blue-800">
                  {formatCurrency(
                    referenceId.summary.totalRedeemAmount,
                    ele.coinType,
                    true
                  )}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-600">
                  Amount in {referenceId.bankAccountDetails.currencyName}
                </div>
                <div className="text-2xl font-bold text-green-600">
                  {formatCurrency(
                    referenceId.amountInCurrency,
                    referenceId.bankAccountDetails.currencySymbol
                  )}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Exchange Rate</div>
                <div className="text-lg font-semibold">
                  1:{referenceId.summary.rate}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Transaction Date</div>
                <div className="text-lg font-semibold">
                  {referenceId.transactionDate
                    ? formatDateByFormat(referenceId.transactionDate)
                    : "N/A"}
                </div>
              </div>
            </div>
          </div>

          {/* Bank Account Details */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
            <div className="flex items-center gap-2 mb-3">
              <MdAccountBalance className="text-gray-700" />
              <h2 className="text-lg font-semibold text-gray-800">
                Bank Account Details
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-gray-600">Account Holder Name</div>
                <div className="font-semibold text-gray-800">
                  {referenceId.bankAccountDetails.accountHolderName}
                </div>
              </div>
              <div>
                <div className="text-gray-600">Account Number</div>
                <div className="font-semibold text-gray-800">
                  {referenceId.bankAccountDetails.accountNumber}
                </div>
              </div>
              <div>
                <div className="text-gray-600">Bank Name</div>
                <div className="font-semibold text-gray-800">
                  {referenceId.bankAccountDetails.bankName}
                </div>
              </div>
              <div>
                <div className="text-gray-600">IFSC Code</div>
                <div className="font-semibold text-gray-800">
                  {referenceId.bankAccountDetails.ifscCode}
                </div>
              </div>
              <div>
                <div className="text-gray-600">Account Type</div>
                <div className="font-semibold text-gray-800">
                  {referenceId.bankAccountDetails.accountType}
                </div>
              </div>
              <div>
                <div className="text-gray-600">Branch</div>
                <div className="font-semibold text-gray-800">
                  {referenceId.bankAccountDetails.branch}
                </div>
              </div>
            </div>
          </div>

          {/* Fees Breakdown */}
          <div className="mb-6 p-4 bg-orange-50 rounded-lg border border-orange-200">
            <h3 className="text-lg font-semibold text-orange-800 mb-3">
              Fee Breakdown
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-700">Redeem Fees:</span>
                <span className="font-medium text-gray-800">
                  {formatCurrency(
                    referenceId.fees.redeemFees,
                    ele.coinType,
                    true
                  )}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">Knowledge Fees:</span>
                <span className="font-medium text-gray-800">
                  {formatCurrency(
                    referenceId.fees.knowledgeFees,
                    ele.coinType,
                    true
                  )}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">Innovation Fees:</span>
                <span className="font-medium text-gray-800">
                  {formatCurrency(
                    referenceId.fees.innovationFees,
                    ele.coinType,
                    true
                  )}
                </span>
              </div>
              <div className="flex justify-between border-t border-orange-300 pt-2 font-semibold text-orange-800">
                <span>Total Fees:</span>
                <span>
                  {formatCurrency(
                    referenceId.fees.totalFees,
                    ele.coinType,
                    true
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* Attachments Section */}
          {(() => {
            const attachments = Array.isArray(referenceId.attachment)
              ? referenceId.attachment
              : typeof referenceId.attachment === "string" && referenceId.attachment
              ? [referenceId.attachment]
              : [];
            return attachments.length > 0 ? (
              <div className="mb-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center gap-2 mb-3">
                  <MdAttachment className="text-purple-600" />
                  <h3 className="text-lg font-semibold text-purple-800">
                    Attachments
                  </h3>
                </div>
                <div className="space-y-2">
                  {attachments.map((url, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="w-20 h-20 flex-shrink-0 bg-white rounded-lg border border-purple-200 overflow-hidden flex items-center justify-center">
                        <img
                          src={url}
                          alt={`Attachment ${index + 1}`}
                          className="max-h-full max-w-full object-contain cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => window.open(url, "_blank")}
                        />
                      </div>
                      <div className="text-sm">
                        <div className="text-gray-700 font-medium">Document {index + 1}</div>
                        <button
                          type="button"
                          onClick={() => window.open(url, "_blank")}
                          className="text-blue-600 hover:text-blue-800 underline"
                        >
                          View Full Size
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null;
          })()}

          {/* Timeline */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-gray-500 border-t pt-4">
            <div>
              <span>Created:</span>
              <span className="ml-2 font-medium">
                {referenceId.createdAt
                  ? formatDateByFormat(referenceId.createdAt)
                  : "N/A"}
              </span>
            </div>
            <div>
              <span>Last Updated:</span>
              <span className="ml-2 font-medium">
                {referenceId.updatedAt
                  ? formatDateByFormat(referenceId.updatedAt)
                  : "N/A"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RedeemDetailsModal;
